import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { supabase } from "../lib/supabase";
import {
  fetchGmailMessages,
  fetchGmailInboxCount,
  archiveGmailMessage,
  archiveGmailMessages,
  fetchGmailMessageBody,
  fetchGmailAttachment,
  fetchGmailThread,
  groupMessagesIntoThreads,
  GmailMessageBody,
  GmailThread,
  GmailThreadDetail,
} from "../lib/gmail";
import { GmailAccount } from "../types";
import { archiveToastLabels, renderArchiveToastText, type ArchiveToastLabels } from "../lib/emailArchiveToast";
import {
  pushUndo,
  removeUndo,
  registerPendingWrite,
  clearPendingWrite,
  UNDO_WINDOW_MS,
} from "../lib/undoStack";

interface GmailState {
  accounts: GmailAccount[];
  activeAccount: string | null; // gmail_email of currently selected account
  // Inbox messages grouped into conversations. The flat message list
  // is collapsed to one entry per threadId so the UI matches Gmail's
  // conversation-grouped inbox.
  threads: GmailThread[];
  // Per-account inbox counts for the Emails button badges — populated
  // independently of the active account's thread list.
  accountCounts: Record<string, number>;
  isLoadingConnection: boolean;
  isFetchingEmails: boolean;
  accessToken: string | null;
  // Accounts whose Google grant was revoked/expired (refresh returned
  // `invalid_grant`). The UI surfaces a reconnect prompt for these instead
  // of silently failing to sync.
  reconnectNeeded: string[];
}

export function useGmail(initialAccountRef?: React.RefObject<string | null>) {
  const [state, setState] = useState<GmailState>({
    accounts: [],
    activeAccount: null,
    threads: [],
    accountCounts: {},
    isLoadingConnection: true,
    isFetchingEmails: false,
    accessToken: null,
    reconnectNeeded: [],
  });

  const [disconnectingEmail, setDisconnectingEmail] = useState<string | null>(null);

  // Ref tracks the latest activeAccount so async callbacks never read stale closures
  const activeAccountRef = useRef<string | null>(null);
  activeAccountRef.current = state.activeAccount;

  // Ref tracks the latest threads list so archiveEmails can snapshot which
  // threads/messages to remove *outside* its setState updater. Computing the
  // snapshot inside the updater via a mutated closure variable is unsafe under
  // React.StrictMode, which invokes functional updaters twice in dev — the
  // second invocation sees the already-filtered list and clobbers the captured
  // snapshot with [].
  const threadsRef = useRef<GmailThread[]>([]);
  threadsRef.current = state.threads;

  // Version counter prevents stale loadConnection calls from overwriting fresher ones.
  // On OAuth redirect, the mount loadConnection (queries before token exists) can finish
  // AFTER the OAuth callback's loadConnection (queries after token is stored), wiping
  // the connected state. This counter ensures only the latest call writes state.
  const loadVersionRef = useRef(0);

  const getValidAccessToken = useCallback(
    async (
      currentToken: string | null,
      tokenExpiry: string,
      gmailEmail: string,
    ): Promise<string | null> => {
      const isExpired =
        new Date(tokenExpiry) < new Date(Date.now() + 60 * 1000);
      if (!isExpired && currentToken) return currentToken;

      const { data, error } = await supabase.functions.invoke("gmail-oauth", {
        body: { action: "refresh", gmail_email: gmailEmail },
      });

      // A revoked/expired Google grant is expected and recoverable. Flag the
      // account so the UI can prompt a reconnect (mirrors the Calendar flow),
      // rather than logging it as a hard failure on every background refresh.
      if (data?.error === "GMAIL_TOKEN_EXPIRED") {
        setState((prev) =>
          prev.reconnectNeeded.includes(gmailEmail)
            ? prev
            : { ...prev, reconnectNeeded: [...prev.reconnectNeeded, gmailEmail] },
        );
        return null;
      }

      if (error || !data?.access_token) {
        console.error("Failed to refresh Gmail token:", error);
        return null;
      }

      // A successful refresh clears any stale reconnect flag for this account.
      setState((prev) => ({
        ...prev,
        accessToken: data.access_token,
        reconnectNeeded: prev.reconnectNeeded.filter((e) => e !== gmailEmail),
      }));
      return data.access_token;
    },
    [],
  );

  // Fetch the Gmail inbox count for every connected account in parallel.
  // Runs after loadConnection and on manual refresh so the per-account
  // badges in the Emails button stay accurate regardless of which account is
  // currently active. Uses labels.get rather than listing messages — one
  // cheap call per account, no 200-message cap.
  const refreshAccountCounts = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return;

    const { data: rows } = await supabase
      .from("gmail_tokens")
      .select("gmail_email, access_token, token_expiry");
    if (!rows || rows.length === 0) {
      setState((prev) => ({ ...prev, accountCounts: {} }));
      return;
    }

    const entries = await Promise.all(
      rows.map(async (row) => {
        const email = row.gmail_email || session.user.email || "unknown";
        try {
          const token = await getValidAccessToken(
            row.access_token,
            row.token_expiry,
            email,
          );
          if (!token) return [email, 0] as const;
          const count = await fetchGmailInboxCount(token);
          return [email, count] as const;
        } catch {
          return [email, 0] as const;
        }
      }),
    );

    setState((prev) => ({
      ...prev,
      accountCounts: Object.fromEntries(entries),
    }));
  }, [getValidAccessToken]);

  const loadConnection = useCallback(async () => {
    const version = ++loadVersionRef.current;

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      if (version === loadVersionRef.current) {
        setState((prev) => ({ ...prev, isLoadingConnection: false }));
      }
      return;
    }

    const { data: rows } = await supabase
      .from("gmail_tokens")
      .select("gmail_email, access_token, token_expiry, avatar_url");

    // A newer loadConnection call started — discard this stale result
    if (version !== loadVersionRef.current) return;

    // For rows with null gmail_email (migration not yet applied), derive it
    // from the authenticated user's email so the rest of the code works uniformly.
    const validRows = (rows || []).map((r) => ({
      ...r,
      gmail_email: r.gmail_email || session.user.email || "unknown",
    }));

    if (validRows.length === 0) {
      setState((prev) => ({
        ...prev,
        accounts: [],
        activeAccount: null,
        isLoadingConnection: false,
      }));
      return;
    }

    // Sort so the JuDI-connected account (the one matching the auth email) comes first
    const authEmail = session.user.email;
    const sortedRows = [...validRows].sort((a, b) => {
      if (a.gmail_email === authEmail) return -1;
      if (b.gmail_email === authEmail) return 1;
      return 0;
    });

    const accounts: GmailAccount[] = sortedRows.map((r) => ({
      gmail_email: r.gmail_email,
      avatar_url: (r.avatar_url as string) ?? null,
    }));

    // Compute the active account once, then use it for both setState and token refresh
    let activeEmail: string = initialAccountRef?.current && accounts.some((a) => a.gmail_email === initialAccountRef.current) ? initialAccountRef.current : accounts[0].gmail_email;
    setState((prev) => {
      const prevActive = prev.activeAccount;
      const stillExists = accounts.some((a) => a.gmail_email === prevActive);
      if (stillExists && prevActive) {
        activeEmail = prevActive;
      }

      const activeRow = validRows.find((r) => r.gmail_email === activeEmail);

      return {
        ...prev,
        accounts,
        activeAccount: activeEmail,
        isLoadingConnection: false,
        accessToken: activeRow?.access_token ?? null,
      };
    });

    // Validate/refresh the token for the active account
    if (version !== loadVersionRef.current) return;

    const activeRow = validRows.find((r) => r.gmail_email === activeEmail);
    if (!activeRow) {
      return;
    }

    const accessToken = await getValidAccessToken(
      activeRow.access_token,
      activeRow.token_expiry,
      activeRow.gmail_email,
    );

    if (version !== loadVersionRef.current) return;

    setState((prev) => ({ ...prev, accessToken }));

    // Populate per-account badges once accounts are known. Fire-and-forget
    // so a slow/failed counts fetch doesn't block the rest of the UI.
    refreshAccountCounts();
  }, [getValidAccessToken, refreshAccountCounts, initialAccountRef]);

  const fetchEmails = useCallback(async () => {
    setState((prev) => ({ ...prev, isFetchingEmails: true }));
    try {
      const currentAccount = activeAccountRef.current;
      if (!currentAccount) {
        setState((prev) => ({ ...prev, isFetchingEmails: false }));
        return;
      }

      // Re-read the token from DB for the active account.
      // Use .or() to also match rows where gmail_email is null (pre-migration).
      const { data: tokenRow } = await supabase
        .from("gmail_tokens")
        .select("access_token, token_expiry")
        .or(`gmail_email.eq.${currentAccount},gmail_email.is.null`)
        .maybeSingle();

      if (!tokenRow) {
        setState((prev) => ({ ...prev, isFetchingEmails: false }));
        return;
      }

      const accessToken = await getValidAccessToken(
        tokenRow.access_token,
        tokenRow.token_expiry,
        currentAccount,
      );

      if (!accessToken) {
        toast.error("Gmail session expired. Please reconnect.");
        setState((prev) => ({
          ...prev,
          isFetchingEmails: false,
        }));
        return;
      }

      const messages = await fetchGmailMessages(accessToken);
      const threads = groupMessagesIntoThreads(messages);
      setState((prev) => ({ ...prev, threads, isFetchingEmails: false }));

      // Manual refresh also refreshes per-account badges so the Emails
      // button stays in sync with what the user sees.
      refreshAccountCounts();
    } catch (error) {
      console.error("Failed to fetch emails:", error);
      toast.error("Failed to load Gmail emails");
      setState((prev) => ({ ...prev, isFetchingEmails: false }));
    }
  }, [getValidAccessToken, refreshAccountCounts]);

  const fetchEmailBody = useCallback(
    async (messageId: string): Promise<GmailMessageBody | null> => {
      const currentAccount = activeAccountRef.current;
      if (!currentAccount) return null;

      const { data: tokenRow } = await supabase
        .from("gmail_tokens")
        .select("access_token, token_expiry")
        .or(`gmail_email.eq.${currentAccount},gmail_email.is.null`)
        .maybeSingle();

      if (!tokenRow) return null;

      const accessToken = await getValidAccessToken(
        tokenRow.access_token,
        tokenRow.token_expiry,
        currentAccount,
      );

      if (!accessToken) {
        toast.error("Gmail session expired. Please reconnect.");
        return null;
      }

      try {
        return await fetchGmailMessageBody(accessToken, messageId);
      } catch (error) {
        console.error("Failed to fetch email body:", error);
        toast.error("Failed to load email");
        return null;
      }
    },
    [getValidAccessToken],
  );

  /**
   * Fetch every message in a Gmail thread (including ones outside the
   * inbox) so the conversation view can show the full back-and-forth.
   */
  const fetchThread = useCallback(
    async (threadId: string): Promise<GmailThreadDetail | null> => {
      const currentAccount = activeAccountRef.current;
      if (!currentAccount) return null;

      const { data: tokenRow } = await supabase
        .from("gmail_tokens")
        .select("access_token, token_expiry")
        .or(`gmail_email.eq.${currentAccount},gmail_email.is.null`)
        .maybeSingle();

      if (!tokenRow) return null;

      const accessToken = await getValidAccessToken(
        tokenRow.access_token,
        tokenRow.token_expiry,
        currentAccount,
      );

      if (!accessToken) {
        toast.error("Gmail session expired. Please reconnect.");
        return null;
      }

      try {
        return await fetchGmailThread(accessToken, threadId);
      } catch (error) {
        console.error("Failed to fetch thread:", error);
        toast.error("Failed to load conversation");
        return null;
      }
    },
    [getValidAccessToken],
  );

  const fetchAttachment = useCallback(
    async (
      messageId: string,
      attachmentId: string,
    ): Promise<Uint8Array | null> => {
      const currentAccount = activeAccountRef.current;
      if (!currentAccount) return null;

      const { data: tokenRow } = await supabase
        .from("gmail_tokens")
        .select("access_token, token_expiry")
        .or(`gmail_email.eq.${currentAccount},gmail_email.is.null`)
        .maybeSingle();

      if (!tokenRow) return null;

      const accessToken = await getValidAccessToken(
        tokenRow.access_token,
        tokenRow.token_expiry,
        currentAccount,
      );

      if (!accessToken) {
        toast.error("Gmail session expired. Please reconnect.");
        return null;
      }

      try {
        return await fetchGmailAttachment(accessToken, messageId, attachmentId);
      } catch (error) {
        console.error("Failed to fetch attachment:", error);
        toast.error("Failed to download attachment");
        return null;
      }
    },
    [getValidAccessToken],
  );

  /**
   * Archive multiple messages with a 5-second undo window.
   *
   * 1. Optimistically updates the local thread list — threads whose
   *    inbox messages are fully archived disappear; threads with some
   *    inbox messages still remaining shrink to those.
   * 2. Resolves a fresh access token (may refresh) — restores on failure.
   * 3. Shows a toast with an "Undo" button for 5 seconds.
   * 4. If the user clicks Undo: restores threads to state, never calls API.
   * 5. If the timer expires: fires archive API calls in the background.
   * 6. If the API fails: restores threads to state with an error toast.
   */
  const archiveEmails = useCallback(
    async (messageIds: string[], options?: ArchiveToastLabels): Promise<void> => {
      if (messageIds.length === 0) return;

      const activeBeforeArchive = activeAccountRef.current;

      // Snapshot the affected threads *outside* the setState updater so
      // the undo path can restore them verbatim. The snapshot must live
      // outside because React.StrictMode double-invokes functional updaters
      // in dev — the second invocation's `prev.threads` is already filtered
      // and would clobber a closure-captured snapshot with [].
      const idSet = new Set(messageIds);
      const previousThreads = threadsRef.current;
      const removedFromThreadIds = new Set<string>();
      let actualRemovedCount = 0;
      for (const thread of previousThreads) {
        const stillIn = thread.messages.filter((m) => !idSet.has(m.id));
        const removed = thread.messages.length - stillIn.length;
        if (removed > 0) {
          actualRemovedCount += removed;
          removedFromThreadIds.add(thread.id);
        }
      }
      if (actualRemovedCount === 0) return;

      setState((prev) => {
        let removedCount = 0;
        const nextThreads: GmailThread[] = [];
        for (const thread of prev.threads) {
          if (!removedFromThreadIds.has(thread.id)) {
            nextThreads.push(thread);
            continue;
          }
          const remaining = thread.messages.filter((m) => !idSet.has(m.id));
          removedCount += thread.messages.length - remaining.length;
          if (remaining.length === 0) continue;
          nextThreads.push({
            ...thread,
            messages: remaining,
            messageCount: remaining.length,
            isUnread: remaining.some((m) => m.isUnread),
            // Refresh latest-message-derived fields when the previous
            // latest is gone so the row keeps showing accurate metadata.
            from: remaining[0].from,
            fromName: remaining[0].fromName,
            subject: remaining[0].subject,
            snippet: remaining[0].snippet,
            date: remaining[0].date,
          });
        }
        const nextCounts =
          activeBeforeArchive && removedCount > 0
            ? {
                ...prev.accountCounts,
                [activeBeforeArchive]: Math.max(
                  0,
                  (prev.accountCounts[activeBeforeArchive] ?? 0) - removedCount,
                ),
              }
            : prev.accountCounts;
        return {
          ...prev,
          threads: nextThreads,
          accountCounts: nextCounts,
        };
      });

      // Resolve a fresh token from the DB rather than trusting state.accessToken
      // (which isn't always re-populated between fetches — causing archives to
      // silently no-op). Uses the same pattern as fetchEmails/fetchEmailBody.
      const restoreEmails = () => {
        setState((prev) => {
          // Restore by reconciling: if a previously-affected thread is
          // missing entirely, re-insert it; if it's still there with
          // fewer messages, top it back up to the original message set.
          const byId = new Map(prev.threads.map((t) => [t.id, t]));
          let restoredCount = 0;
          for (const original of previousThreads) {
            if (!removedFromThreadIds.has(original.id)) continue;
            const current = byId.get(original.id);
            if (!current) {
              byId.set(original.id, original);
              restoredCount += original.messages.length;
            } else {
              const currentIds = new Set(current.messages.map((m) => m.id));
              const merged = [...current.messages];
              for (const msg of original.messages) {
                if (!currentIds.has(msg.id)) {
                  merged.push(msg);
                  restoredCount += 1;
                }
              }
              merged.sort((a, b) => {
                const ta = new Date(a.date).getTime() || 0;
                const tb = new Date(b.date).getTime() || 0;
                return tb - ta;
              });
              byId.set(original.id, {
                ...original,
                messages: merged,
                messageCount: merged.length,
                isUnread: merged.some((m) => m.isUnread),
              });
            }
          }
          if (restoredCount === 0) return prev;
          const nextThreads = Array.from(byId.values()).sort((a, b) => {
            const ta = new Date(a.date).getTime() || 0;
            const tb = new Date(b.date).getTime() || 0;
            return tb - ta;
          });
          const nextCounts = activeBeforeArchive
            ? {
                ...prev.accountCounts,
                [activeBeforeArchive]:
                  (prev.accountCounts[activeBeforeArchive] ?? 0) + restoredCount,
              }
            : prev.accountCounts;
          return {
            ...prev,
            threads: nextThreads,
            accountCounts: nextCounts,
          };
        });
      };

      let token: string;
      try {
        const currentAccount = activeBeforeArchive;
        if (!currentAccount) {
          restoreEmails();
          toast.error("No active Gmail account");
          return;
        }

        const { data: tokenRow } = await supabase
          .from("gmail_tokens")
          .select("access_token, token_expiry")
          .or(`gmail_email.eq.${currentAccount},gmail_email.is.null`)
          .maybeSingle();

        if (!tokenRow) {
          restoreEmails();
          toast.error("Gmail session expired. Please reconnect.");
          return;
        }

        const validToken = await getValidAccessToken(
          tokenRow.access_token,
          tokenRow.token_expiry,
          currentAccount,
        );

        if (!validToken) {
          restoreEmails();
          toast.error("Gmail session expired. Please reconnect.");
          return;
        }

        token = validToken;
      } catch (err) {
        console.error("Failed to resolve Gmail token for archive:", err);
        restoreEmails();
        toast.error("Failed to archive — please try again.");
        return;
      }

      // Unique key for this batch so concurrent undos don't collide.
      const batchKey = `${Date.now()}-${messageIds.join(",")}`;
      let undone = false;
      let fired = false;

      const { pending: label, success: successLabel } = archiveToastLabels(messageIds.length, options);

      const toastId = `archive-${batchKey}`;

      const doArchive = ({ keepalive = false }: { keepalive?: boolean } = {}) => {
        if (fired) return;
        fired = true;
        clearPendingWrite(batchKey);
        // One request covers the whole batch via batchModify — sending N
        // parallel modify calls trips Gmail's concurrent-request cap (429
        // RESOURCE_EXHAUSTED) for larger selections. Single-message archives
        // still use messages/modify since batchModify has more overhead.
        const request =
          messageIds.length === 1
            ? archiveGmailMessage(token, messageIds[0], { keepalive })
            : archiveGmailMessages(token, messageIds, { keepalive });
        request.then(
          () => {
            toast.success(renderArchiveToastText(successLabel, toastId), { id: toastId, duration: 3000 });
          },
          () => {
            restoreEmails();
            toast.error(
              messageIds.length === 1
                ? "Failed to archive 1 email"
                : `Failed to archive ${messageIds.length} emails`,
              { id: toastId, duration: 4000 },
            );
          },
        );
      };

      // Schedule the actual API call after the undo window.
      const timer = setTimeout(() => {
        removeUndo(undoEntry);
        doArchive();
      }, UNDO_WINDOW_MS);
      registerPendingWrite(batchKey, {
        timer,
        flush: () => doArchive({ keepalive: true }),
      });

      const undo = () => {
        if (undone || fired) return;
        undone = true;
        removeUndo(undoEntry);
        clearPendingWrite(batchKey);
        clearTimeout(timer);
        restoreEmails();
      };
      const undoEntry = { undo, toastId };

      // Single toast for the entire archive lifecycle: starts with an undo
      // prompt, then updates in-place to success/error when the API resolves.
      toast(
        (t) => (
          <span className="flex items-center gap-3 text-sm">
            <span>{renderArchiveToastText(label, t.id)}</span>
            <button
              type="button"
              onClick={() => {
                undo();
                toast.dismiss(t.id);
              }}
              className="font-semibold underline underline-offset-2 whitespace-nowrap"
            >
              Undo
            </button>
            <span className="opacity-50 text-xs whitespace-nowrap">
              press <kbd className="font-semibold">U</kbd>
            </span>
          </span>
        ),
        { id: toastId, duration: Infinity },
      );

      pushUndo(undoEntry);
    },
    [getValidAccessToken],
  );

  const connectGmail = useCallback(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error("Gmail integration is not configured.");
      return;
    }

    const scope = [
      "openid",
      "profile",
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://www.googleapis.com/auth/gmail.modify",
    ].join(" ");

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: window.location.origin,
      response_type: "code",
      scope,
      access_type: "offline",
      prompt: "consent",
      state: "gmail_oauth",
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }, []);

  const disconnectGmail = useCallback(async (gmailEmail: string) => {
    setDisconnectingEmail(gmailEmail);
    try {
      await supabase.functions.invoke("gmail-oauth", {
        body: { action: "disconnect", gmail_email: gmailEmail },
      });

      setState((prev) => {
        const remaining = prev.accounts.filter((a) => a.gmail_email !== gmailEmail);
        const newActive =
          remaining.length > 0
            ? prev.activeAccount === gmailEmail
              ? remaining[0].gmail_email
              : prev.activeAccount
            : null;

        const nextCounts = { ...prev.accountCounts };
        delete nextCounts[gmailEmail];

        return {
          ...prev,
          accounts: remaining,
          activeAccount: newActive,
          threads: prev.activeAccount === gmailEmail ? [] : prev.threads,
          accessToken: prev.activeAccount === gmailEmail ? null : prev.accessToken,
          accountCounts: nextCounts,
          reconnectNeeded: prev.reconnectNeeded.filter((e) => e !== gmailEmail),
        };
      });

      toast.success("Gmail disconnected");
    } finally {
      setDisconnectingEmail(null);
    }
  }, []);

  const setActiveAccount = useCallback((gmailEmail: string) => {
    setState((prev) => ({
      ...prev,
      activeAccount: gmailEmail,
      threads: [],
      accessToken: null,
    }));
  }, []);

  const handleOAuthCallback = useCallback(
    async (code: string) => {
      try {
        const { data, error } = await supabase.functions.invoke("gmail-oauth", {
          body: {
            action: "exchange",
            code,
            redirectUri: window.location.origin,
          },
        });

        // Edge function may signal failure via FunctionsHttpError (which
        // carries the response in `error.context`) or via a 200 body with
        // `{error}`. Surface the real message in either case so the user
        // knows what actually went wrong.
        if (error) {
          let detail = error.message;
          try {
            const ctx = (error as { context?: Response }).context;
            if (ctx) {
              const parsed = await ctx.json();
              if (parsed?.error) detail = parsed.error;
            }
          } catch {
            /* fall through to error.message */
          }
          throw new Error(detail);
        }
        if (data?.error) throw new Error(data.error);

        await loadConnection();
        toast.success("Gmail connected successfully!");
      } catch (error) {
        console.error("OAuth callback failed:", error);
        const message =
          error instanceof Error ? error.message : "Failed to connect Gmail";
        toast.error(message);
      }
    },
    [loadConnection],
  );

  // Fetch emails whenever the active account changes
  useEffect(() => {
    if (state.activeAccount && state.accounts.length > 0) {
      fetchEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the active account alone. `state.accounts.length` is read as a guard, so listing it would refetch the same mailbox whenever another account is added or removed; `fetchEmails` reads the active account from a ref rather than its closure, so its identity changing carries no new information either.
  }, [state.activeAccount]);

  // One-shot bootstrap of the stored connection. `loadConnection` is listed
  // rather than suppressed: its whole `useCallback` chain bottoms out in
  // `getValidAccessToken` (no deps), so its identity never changes for a
  // mounted instance and this runs exactly once, mount-only. The one path that
  // needs a reload afterwards — finishing the OAuth callback — awaits it
  // directly.
  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  return {
    accounts: state.accounts,
    activeAccount: state.activeAccount,
    accountCounts: state.accountCounts,
    reconnectNeeded: state.reconnectNeeded,
    isConnected: state.accounts.length > 0,
    threads: state.threads,
    isLoadingConnection: state.isLoadingConnection,
    isFetchingEmails: state.isFetchingEmails,
    setActiveAccount,
    fetchEmails,
    fetchEmailBody,
    fetchThread,
    fetchAttachment,
    archiveEmails,
    connectGmail,
    disconnectGmail,
    disconnectingEmail,
    handleOAuthCallback,
    reloadAccounts: loadConnection,
  };
}
